import { inject, Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, Input, ViewChild, ElementRef, HostBinding, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { DynamicChartHoverEvent, DynamicChartViewConfig } from '@dataunveil/ducharts';
import { FilterService } from 'src/app/services/filter.service';
import { NewFilterService } from 'src/app/services/new-filter.service';
import { DataAggregationService, AggregationConfig } from 'src/app/services/data-aggregation.service';
import { NoDataNotifierService } from 'src/app/services/no-data-notifier.service';

/**
 * ViewConfig keys of <duc-dynamic-chart> that are read straight from item.config.
 * Dropdown values ({ name, value }) are unwrapped and JSON strings are parsed, so any of
 * these can be added to the widget config without a code change.
 */
const PASSTHROUGH_VIEW_KEYS: (keyof DynamicChartViewConfig)[] = [
  'stacked_value', 'leftSliderName', 'rightSliderName', 'margins',
  'barKeys', 'lineKeys', 'areaKeys', 'autoKeys', 'barMode', 'stackedArea', 'palette',
  'dataFormat', 'period', 'barAggregate', 'lineAggregate',
  'rightAxisKeys', 'lineOnPrimaryAxis', 'yAxisRightLabel', 'yAxisRightFormatting',
  'yAxisRightTickValues', 'yAxisRightFixedMax', 'yAxisRightTickSuffix',
  'yAxisLabelColour', 'yAxisRightLabelColour', 'yAxisTickColour', 'yAxisRightTickColour', 'xAxisTickColour',
  'barRadius', 'barWidthRatio', 'barPadding', 'lineWidth', 'lineDash', 'lineDot', 'areaOpacity',
  'partialPeriod', 'partialPeriodLabel', 'animate', 'dataLabels', 'hoverLine', 'showSlider',
  'orientation', 'yAxisSort', 'categoryFormatting', 'showTooltip', 'tooltipTitle', 'valueDisplay',
  'noDataMessage', 'yAxisFormatting', 'showBrush', 'brushMinPointWidth', 'showLegend', 'legendInteractive',
  'axisFontSize', 'axisFontWeight', 'colorScheme', 'highlight',
] as any;

/** portal yAxisType dropdown -> ducharts category axis type */
const Y_AXIS_TYPE_MAP: Record<string, 'band' | 'linear' | 'time'> = {
  string: 'band', band: 'band', category: 'band',
  number: 'linear', numeric: 'linear', linear: 'linear',
  date: 'time', time: 'time',
};

@Component({
  selector: 'app-dynamic-charts',
  templateUrl: './dynamic-charts.component.html',
  styleUrls: ['./dynamic-charts.component.scss'],
  standalone: false
})
export class DynamicChartsComponent implements OnInit, OnDestroy, OnChanges {
  private readonly noDataNotifier = inject(NoDataNotifierService);

  @Input('data') inputData: any;
  @Input('item') item: any;
  @Input('pageKey') pageKey: any;
  @Input('config') config: any;
  @Input('headerConfig') headerConfig: any;
  @Input('error_name') error_name: any;
  @Input('heading') heading: string = '';
  @Output() chartHover = new EventEmitter<DynamicChartHoverEvent>();

  @ViewChild('fs', { static: true }) fs!: ElementRef;
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @HostBinding('class.is-fullscreen') isFullscreen = false;
  isActive = false;
  isLoading: boolean = true;
  noData: boolean = false;
  iconList: any = [];
  initiateChart: boolean = false;
  chartData: any = [];
  props: any = { chartHeight: 400 };
  subs: Subscription[] = [];

  /** config handed to <duc-dynamic-chart>; a new object re-renders the chart */
  ducConfig: { view: DynamicChartViewConfig } = { view: {} };

  private static instanceCounter = 0;
  divId: any = 'dynamicChart_' + (++DynamicChartsComponent.instanceCounter);
  /** Show By label the current view was built with (see syncShowByAxisLabels). */
  private appliedShowByLabel: string | null = null;

  constructor(private filterService: FilterService, private dataAggregation: DataAggregationService, private newFilterService: NewFilterService) {
    this.subs.push(
      this.filterService.filterQuery.subscribe(() => {
        this.isLoading = true;
      })
    );
    // Show By reloads data just like a filter change, so show the loader the same way
    this.subs.push(
      this.newFilterService.showBy.subscribe(() => {
        this.isLoading = true;
        this.noData = false;
        this.syncShowByAxisLabels();
      })
    );
  }

  @HostListener('fullscreenchange')
  @HostListener('webkitfullscreenchange')
  @HostListener('mozfullscreenchange')
  @HostListener('MSFullscreenChange')
  screenChange() {
    if (!document.fullscreenElement) {
      this.closeFullscreen();
    }
  }

  fullscreenChanges(fullscreen: any) {
    if (fullscreen == false) {
      this.closeFullscreen();
      return;
    }
    this.props.chartHeight = window.outerHeight - 60;
    this.isFullscreen = fullscreen;
    this.isActive = fullscreen;
    this.buildDucConfig();
  }

  closeFullscreen(): void {
    this.props.chartHeight = this.defaultHeight();
    this.isFullscreen = false;
    this.isActive = false;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    this.buildDucConfig();
  }

  // ── Lifecycle ──
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputData'] && changes['inputData'].currentValue != changes['inputData'].previousValue && this.initiateChart) {
      this.getChartData();
    }
  }

  ngOnInit(): void {
    this.iconList = this.item?.config?.icon ? this.item.config.icon : this.iconList;
    this.divId = this.item?.config?.divId ? this.item.config.divId + this.item.widget_uniq_key : this.divId;
    this.props.chartHeight = this.defaultHeight();
    this.buildDucConfig();

    this.initiateChart = true;
    if (this.inputData?.length) this.getChartData();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ── Data ──
  getChartData(): void {
    const rows = Array.isArray(this.inputData) ? this.inputData : [];
    if (rows.length === 0) {
      this.chartData = [];
      this.isLoading = false;
      this.noData = true; this.noDataNotifier.report('dynamic-charts');
      return;
    }

    const cfg = this.item?.config || {};
    this.chartData = cfg['AggregationEnabled'] === true ? this.aggregateRows(rows, cfg) : rows;
    this.syncShowByAxisLabels();
    console.log('[DynamicCharts] final chart data', this.item?.config?.report_heading, this.chartData);
    this.noData = this.chartData.length === 0;
    this.isLoading = false;
    if (this.noData) this.noDataNotifier.report('dynamic-charts');
  }

  /**
   * Group rows with the widget's aggregation_* config before handing them to the chart.
   * aggregation_timeSeries { orderBy, order: 'desc' | 'asc' (default 'desc'), limit } then
   * sorts the groups and keeps the first `limit` — e.g. the top 10 payers by patient count.
   */
  private aggregateRows(rows: any[], cfg: any): any[] {
    const groupBy = cfg['aggregation_groupBy'];
    const aggCfg: AggregationConfig = {
      groupBy: typeof groupBy === 'string' && groupBy.includes(',')
        ? groupBy.split(',').map((g: string) => g.trim())
        : (groupBy || undefined),
      fields: this.parseJson(cfg['aggregation_fields']) || {},
      computed: this.parseJson(cfg['aggregation_computed']) || undefined,
    };
    let groups = this.dataAggregation.aggregate(rows, aggCfg).groups;

    const ts = this.parseJson(cfg['aggregation_timeSeries']);
    if (ts && typeof ts === 'object') {
      if (ts.orderBy) {
        const dir = ts.order === 'asc' ? 1 : -1;
        groups = [...groups].sort((a, b) => {
          const va = a?.[ts.orderBy], vb = b?.[ts.orderBy];
          return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
        });
      }
      const limit = Number(ts.limit);
      if (limit > 0) groups = groups.slice(0, limit);
    }

    const rowsOpt = this.parseJson(cfg['aggregation_rows']) ?? ts?.rows;
    if (rowsOpt) return this.selectSourceRows(rows, groups, rowsOpt, aggCfg);

    const unpivot = this.parseJson(cfg['aggregation_unpivot']) ?? ts?.unpivot;
    return unpivot ? this.unpivotRows(groups, unpivot, aggCfg) : groups;
  }

  /**
   * Back to the raw rows, limited to the groups that survived the sort/limit (e.g. the top 10
   * payers), then filtered and trimmed:
   *   rows: { where: { MISMATCH: 1 }, columns: ['DU_PATIENT_ID', 'FIRST_PAYER_NAME', ...] }
   * `where` compares as trimmed strings, so 1 matches "1"; an array value matches any of its items.
   */
  private selectSourceRows(rows: any[], groups: any[], opts: any, aggCfg: AggregationConfig): any[] {
    const groupBy = aggCfg.groupBy ? ([] as string[]).concat(aggCfg.groupBy) : [];
    const groupKey = (r: any) => groupBy.map(f => String(r?.[f] ?? 'unknown')).join('|');
    const keptGroups = new Set(groups.map(groupKey));

    const norm = (v: any) => String(v ?? '').trim();
    const where: Record<string, any> = opts?.where || {};
    const matches = (r: any) => Object.entries(where).every(([col, want]) =>
      ([] as any[]).concat(want).some(w => norm(r?.[col]) === norm(w)));

    const columns: string[] | undefined = opts?.columns;
    return rows
      .filter(r => (!groupBy.length || keptGroups.has(groupKey(r))) && matches(r))
      .map(r => {
        if (!columns?.length) return r;
        const out: any = {};
        columns.forEach(c => out[c] = r?.[c]);
        return out;
      });
  }

  /**
   * One wide row per group -> one long row per bar:
   *   { LATEST_PAYER_NAME, total_patients, 'Commercially Filled': 45.2, ... }
   *   -> { LATEST_PAYER_NAME, total_patients, name: 'Commercially Filled', value: 45.2 }, ...
   * unpivot: { keys (default: every computed field), keep (default: groupBy fields),
   *            categoryName (default 'name'), valueName (default 'value') }
   */
  private unpivotRows(groups: any[], unpivot: any, aggCfg: AggregationConfig): any[] {
    const opts = typeof unpivot === 'object' ? unpivot : {};
    const keys: string[] = opts.keys || Object.keys(aggCfg.computed || {});
    const groupBy = aggCfg.groupBy ? ([] as string[]).concat(aggCfg.groupBy) : [];
    const keep: string[] = [...new Set([...groupBy, ...(opts.keep || [])])];
    const categoryName = opts.categoryName || 'name';
    const valueName = opts.valueName || 'value';

    return groups.flatMap(g => {
      const base: any = {};
      keep.forEach(k => base[k] = g[k]);
      return keys.map(k => ({ ...base, [categoryName]: k, [valueName]: g[k] ?? 0 }));
    });
  }

  // ── Config: item.config -> <duc-dynamic-chart> view ──
  buildDucConfig(): void {
    const cfg = this.item?.config || {};
    // Show By label (only published by a Show By card flagged showBy_agg_level) fills
    // axis titles the designer left empty; designer labels always win
    const showByLabel = this.filterService.showByAggLabel || '';
    this.appliedShowByLabel = this.filterService.showByAggLabel;

    const view: any = {
      chartType: this.unwrap(cfg['chartType'] ?? cfg['chart_type']) || 'clustered',
      x_axis: this.column(cfg['x_axis']) || 'period',
      y_axis: this.column(cfg['y_axis']) || 'value',
      category_name: this.column(cfg['category_name']) || 'name',
      xAxisLabel: cfg['xAxisLabel'] || showByLabel || cfg['x_axis']?.['api_resp_column_display_nm'] || '',
      yAxisLabel: cfg['yAxisLabel'] || showByLabel || cfg['y_axis']?.['api_resp_column_display_nm'] || '',
      colours: this.parseJson(cfg['colours']) || undefined,
      valueMode: this.unwrap(cfg['valueMode']) || 'value',
      chartHeight: this.props.chartHeight,
    };

    const yAxisType = this.unwrap(cfg['yAxisType']);
    if (yAxisType) view.yAxisType = Y_AXIS_TYPE_MAP[String(yAxisType).toLowerCase()] || yAxisType;
    // category axis type of a vertical chart, same values as yAxisType
    const xAxisType = this.unwrap(cfg['yAxisType']);
    if (xAxisType) view.xAxisType = Y_AXIS_TYPE_MAP[String(xAxisType).toLowerCase()] || xAxisType;

    for (const key of PASSTHROUGH_VIEW_KEYS) {
      const value = this.parseJson(this.unwrap(cfg[key]));
      if (value !== undefined && value !== null && value !== '') view[key] = value;
    }

    // drop undefined so ducharts defaults apply
    Object.keys(view).forEach(k => view[k] === undefined && delete view[k]);
    this.ducConfig = { view };
  }

  /** Rebuild the view only when the Show By label used for the axis titles has changed. */
  private syncShowByAxisLabels(): void {
    if (this.filterService.showByAggLabel !== this.appliedShowByLabel) this.buildDucConfig();
  }

  private defaultHeight(): number {
    return Number(this.item?.config?.['chartHeight']) || 400;
  }

  /** dropdown config values arrive as { name, value } */
  private unwrap(v: any): any {
    return v && typeof v === 'object' && !Array.isArray(v) && 'value' in v ? v.value : v;
  }

  /** column config can be a plain name or an API column object */
  private column(v: any): string {
    if (!v) return '';
    if (typeof v === 'string') return v;
    return v['api_resp_column_nm'] || v['value'] || '';
  }

  /** JSON stored as a string in config (e.g. aggregation_fields) */
  private parseJson(v: any): any {
    if (typeof v !== 'string') return v;
    const s = v.trim();
    if (!s.startsWith('{') && !s.startsWith('[')) return v;
    try { return JSON.parse(s); } catch { return undefined; }
  }
}
